import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import './BatchResults.css';
import ProductGrid from './ProductGrid';
import { analyticsService } from '../services/analyticsService';
import { downloadBatchData } from '../services/exportService';
import { importBatchData } from '../services/importService';

const StatItem = ({ value, label }) => (
  <div className="stat-item">
    <span className="stat-value">{value}</span>
    <span className="stat-label">{label}</span>
  </div>
);

const BatchSummary = ({ totalLinksProcessed, validStoresFound, storesWithFreeProducts, onExport, isExporting, exportProgress }) => (
  <div className="batch-summary">
    <div className="batch-header">
      <h3>Batch Processing Results</h3>
      <button 
        className="export-button"
        onClick={onExport}
        disabled={isExporting}
        title="Export results to CSV file"
      >
        {isExporting ? (
          <>
            <i className="fas fa-spinner fa-spin"></i>
            {` ${Math.round(exportProgress)}%`}
          </>
        ) : (
          <>
            <i className="fas fa-upload"></i> Export
          </>
        )}
      </button>
    </div>
    <div className="stats-container">
      <StatItem value={totalLinksProcessed} label="Links Processed" />
      <StatItem value={validStoresFound} label="Valid Stores" />
      <StatItem value={storesWithFreeProducts} label="Stores with Free Products" />
    </div>
    {isExporting && (
      <div className="export-progress">
        <div 
          className="progress-bar" 
          style={{ width: `${exportProgress}%` }}
        />
      </div>
    )}
  </div>
);

const StoreSection = React.memo(({ store, isExpanded, onToggle, searchQuery, productCount }) => {
  const [currentProductPage, setCurrentProductPage] = useState(1);
  const productsPerPage = 100;
  const [showCopied, setShowCopied] = useState(false);
  const [isFavorite, setIsFavorite] = useState(() => {
    const favorites = JSON.parse(localStorage.getItem('favoriteStores') || '[]');
    return favorites.includes(store.storeUrl);
  });

  // Reset pagination when store is collapsed
  useEffect(() => {
    if (!isExpanded) {
      setCurrentProductPage(1);
    }
  }, [isExpanded]);

  const handleCopyLink = useCallback((e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(store.storeUrl).then(() => {
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    });
  }, [store.storeUrl]);

  const handleToggleFavorite = useCallback((e) => {
    e.stopPropagation();
    setIsFavorite(prev => {
      const newState = !prev;
      const favorites = JSON.parse(localStorage.getItem('favoriteStores') || '[]');
      
      if (newState) {
        // Add to favorites if not already present
        if (!favorites.includes(store.storeUrl)) {
          favorites.push(store.storeUrl);
        }
      } else {
        // Remove from favorites
        const index = favorites.indexOf(store.storeUrl);
        if (index > -1) {
          favorites.splice(index, 1);
        }
      }
      
      localStorage.setItem('favoriteStores', JSON.stringify(favorites));
      return newState;
    });
  }, [store.storeUrl]);

  const highlightMatches = useCallback((text) => {
    if (!searchQuery) return text;
    const query = searchQuery.toLowerCase().trim();
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }, [searchQuery]);

  const showAllProducts = localStorage.getItem('showAllProducts') === 'true';
  const activeStoreProducts = showAllProducts && store.allProducts?.length > 0 
    ? store.allProducts 
    : store.freeProducts;

  const paginatedProducts = useMemo(() => {
    if (!isExpanded || !activeStoreProducts) return [];
    const startIndex = (currentProductPage - 1) * productsPerPage;
    return activeStoreProducts.slice(startIndex, startIndex + productsPerPage);
  }, [isExpanded, activeStoreProducts, currentProductPage, productsPerPage]);

  const totalPages = activeStoreProducts 
    ? Math.ceil(activeStoreProducts.length / productsPerPage)
    : Math.ceil(productCount / productsPerPage);

  const ProductPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="product-pagination">
        <div className="pagination-info">
          Showing {((currentProductPage - 1) * productsPerPage) + 1}-{Math.min(currentProductPage * productsPerPage, activeStoreProducts?.length || productCount)} of {activeStoreProducts?.length || productCount} products
        </div>
        <div className="pagination">
          <button
            className="page-button"
            onClick={() => setCurrentProductPage(1)}
            disabled={currentProductPage === 1}
          >
            <i className="fas fa-angle-double-left"></i>
          </button>
          <button
            className="page-button"
            onClick={() => setCurrentProductPage(prev => Math.max(1, prev - 1))}
            disabled={currentProductPage === 1}
          >
            <i className="fas fa-angle-left"></i>
          </button>
          <span className="page-info">
            Page {currentProductPage} of {totalPages}
          </span>
          <button
            className="page-button"
            onClick={() => setCurrentProductPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentProductPage === totalPages}
          >
            <i className="fas fa-angle-right"></i>
          </button>
          <button
            className="page-button"
            onClick={() => setCurrentProductPage(totalPages)}
            disabled={currentProductPage === totalPages}
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="store-section">
      <div 
        className="store-title"
        onClick={() => onToggle(store.storeUrl)}
      >
        <div className="store-url-container">
          <span 
            className="store-url" 
            dangerouslySetInnerHTML={{ __html: highlightMatches(store.storeUrl) }}
          />
          <div className="store-actions">
            <button
              className="favorite-button"
              onClick={handleToggleFavorite}
              title={isFavorite ? "Remove from favorites" : "Add to favorites"}
            >
              <i className={`${isFavorite ? 'fas fa-star favorite-active' : 'far fa-star favorite-inactive'}`}></i>
            </button>
            <button
              className="copy-link-button"
              onClick={handleCopyLink}
              title="Copy store URL"
            >
              <i className={`fas ${showCopied ? 'fa-check' : 'fa-copy'}`}></i>
              {showCopied && <span className="copied-tooltip">Copied!</span>}
            </button>
          </div>
        </div>
        <div className="store-title-right">
          <span className="product-count">
            {store.freeProducts ? store.freeProducts.length : productCount} Free Products
          </span>
          <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
        </div>
      </div>
      {isExpanded && activeStoreProducts && (
        <div className="store-content">
          <div className="stores-grid">
            {totalPages > 1 && <ProductPagination />}
            <ProductGrid 
              products={paginatedProducts}
              allProducts={showAllProducts ? paginatedProducts : undefined}
              isValidStore={true}
            />
            {totalPages > 1 && <ProductPagination />}
          </div>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for React.memo
  return (
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.searchQuery === nextProps.searchQuery &&
    prevProps.store === nextProps.store &&
    prevProps.productCount === nextProps.productCount
  );
});

// Optimized debounce utility
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Skip debounce for empty strings or single characters
    if (!value || value.length <= 1) {
      setDebouncedValue(value);
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
      timeoutRef.current = null;
    }, delay);

    // Cleanup on unmount or value change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
};

const LoadingState = () => (
  <div className="batch-loading">
    <div className="loading-spinner">
      <i className="fas fa-spinner fa-spin"></i>
    </div>
    <h3>Processing Results</h3>
    <p>Please wait while we organize your results...</p>
  </div>
);

const BatchResults = ({ batchData, onImportResults }) => {
  const [expandedStores, setExpandedStores] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [storesPerPage] = useState(50);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const debouncedSearchQuery = useDebounce(searchQuery, 150);
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const prevSearchQuery = useRef(debouncedSearchQuery);
  const [storeDetailsCache, setStoreDetailsCache] = useState(new Map());
  const maxExpandedStores = 5;

  // Initialize loading state when batchData changes
  useEffect(() => {
    if (batchData?.results) {
      const timer = setTimeout(() => {
        setIsLoading(false);
        // Record analytics when batch processing completes
        analyticsService.recordBatchSearch(batchData, 0, false);
        // Clear any existing cache when new data is loaded
        setStoreDetailsCache(new Map());
        setExpandedStores(new Set());
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [batchData]);

  // Clear cache for stores that are no longer expanded
  useEffect(() => {
    setStoreDetailsCache(prevCache => {
      const newCache = new Map(prevCache);
      for (const [storeUrl] of newCache) {
        if (!expandedStores.has(storeUrl)) {
          newCache.delete(storeUrl);
        }
      }
      return newCache;
    });
  }, [expandedStores]);

  // Collapse all stores when search query changes
  useEffect(() => {
    if (prevSearchQuery.current !== debouncedSearchQuery) {
      setExpandedStores(new Set());
      setStoreDetailsCache(new Map());
      prevSearchQuery.current = debouncedSearchQuery;
    }
  }, [debouncedSearchQuery]);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  // Optimize the filtered results with simpler, faster search
  const filteredResults = useMemo(() => {
    if (!batchData?.results) {
      return [];
    }

    // First, get the showOutOfStock setting from localStorage
    const storedShowOutOfStock = localStorage.getItem('showOutOfStock');
    const showOutOfStockSetting = storedShowOutOfStock === null ? true : storedShowOutOfStock === 'true';

    // Create a memoized search query for better performance
    const query = debouncedSearchQuery.toLowerCase().trim();
    const hasSearchQuery = query.length > 0;

    // Filter out stores with only out-of-stock items when showOutOfStock is false
    let results = showOutOfStockSetting 
      ? batchData.results 
      : batchData.results.filter(store => 
          store.freeProducts?.some(product => 
            product.freeVariants.some(variant => variant.available)
          )
        );

    // Only apply search if there's a query
    if (hasSearchQuery) {
      // Split search terms for better matching
      const searchTerms = query.split(/\s+/);
      
      results = results.filter(store => {
        // Quick check for store URL first (most common match)
        const storeUrl = store.storeUrl.toLowerCase();
        if (searchTerms.every(term => storeUrl.includes(term))) {
          return true;
        }
        
        // Only check products if store URL doesn't match
        if (!store.freeProducts) return false;

        // Use some() for early termination when a match is found
        return store.freeProducts.some(product => {
          const title = product.title.toLowerCase();
          // Check if all search terms are in the title
          return searchTerms.every(term => title.includes(term));
        });
      });
    }

    return results;
  }, [batchData?.results, debouncedSearchQuery]);

  // Only render visible stores
  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * storesPerPage;
    return filteredResults.slice(startIndex, startIndex + storesPerPage);
  }, [filteredResults, currentPage, storesPerPage]);

  const totalPages = Math.ceil(filteredResults.length / storesPerPage);

  const toggleStore = useCallback((storeUrl) => {
    setExpandedStores(prev => {
      const newSet = new Set(prev);
      if (newSet.has(storeUrl)) {
        newSet.delete(storeUrl);
        return newSet;
      } else {
        // If we're at the maximum, remove the oldest expanded store
        if (newSet.size >= maxExpandedStores) {
          const [firstStore] = newSet;
          newSet.delete(firstStore);
        }
        newSet.add(storeUrl);
        return newSet;
      }
    });

    // Update store details cache
    setStoreDetailsCache(prevCache => {
      const newCache = new Map(prevCache);
      if (newCache.has(storeUrl)) {
        newCache.delete(storeUrl);
      } else {
        const storeData = batchData.results.find(s => s.storeUrl === storeUrl);
        if (storeData) {
          newCache.set(storeUrl, storeData);
        }
      }
      return newCache;
    });
  }, [batchData?.results]);

  // Function to clear all expanded stores and their cached data
  const clearMemory = useCallback(() => {
    setExpandedStores(new Set());
    setStoreDetailsCache(new Map());
  }, []);

  // Add memory usage warning when too many stores are expanded
  const memoryWarning = expandedStores.size >= maxExpandedStores - 1 
    ? `Memory usage is high. Consider collapsing some stores.` 
    : '';

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    setExportProgress(0);

    try {
        await downloadBatchData(batchData, (progress) => {
            setExportProgress(progress);
        });

    } catch (error) {
        console.error('Export error:', error);
        alert('Error exporting data. Please try again.');
    } finally {
        setIsExporting(false);
        setExportProgress(0);
    }
}, [batchData]);

  const handleImport = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);
    setImportProgress(0);
    try {
      const importedData = await importBatchData(file, (progress) => {
        setImportProgress(progress);
      });
      onImportResults(importedData);
    } catch (error) {
      console.error('Import error:', error);
      alert(error.message);
    } finally {
      setIsImporting(false);
      setImportProgress(0);
      event.target.value = '';
    }
  }, [onImportResults]);

  // Optimize search input handling with useCallback
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    // Only update if the value has changed
    setSearchQuery(prev => {
      if (prev !== value) {
        setCurrentPage(1);
        return value;
      }
      return prev;
    });
  }, []);

  // Optimize clear search with useCallback
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setCurrentPage(1);
  }, []);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of results
    window.scrollTo({
      top: document.querySelector('.batch-results')?.offsetTop - 20 || 0,
      behavior: 'smooth'
    });
  };

  const Pagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="pagination">
        <button 
          className="page-button"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          <i className="fas fa-angle-double-left"></i>
        </button>
        <button 
          className="page-button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <i className="fas fa-angle-left"></i>
        </button>
        
        {startPage > 1 && (
          <>
            <button 
              className="page-button"
              onClick={() => handlePageChange(1)}
            >
              1
            </button>
            {startPage > 2 && <span className="page-ellipsis">...</span>}
          </>
        )}

        {pageNumbers.map(number => (
          <button
            key={number}
            className={`page-button ${currentPage === number ? 'active' : ''}`}
            onClick={() => handlePageChange(number)}
          >
            {number}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="page-ellipsis">...</span>}
            <button 
              className="page-button"
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}

        <button 
          className="page-button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <i className="fas fa-angle-right"></i>
        </button>
        <button 
          className="page-button"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <i className="fas fa-angle-double-right"></i>
        </button>
      </div>
    );
  };

  if (!batchData?.success || !batchData.results) {
    return (
      <div className="batch-results">
        <div className="import-section">
          <label htmlFor="import-results" className="import-button">
            {isImporting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {` ${Math.round(importProgress)}%`}
              </>
            ) : (
              <>
                <i className="fas fa-download"></i> Import Results
              </>
            )}
          </label>
          <input
            id="import-results"
            type="file"
            accept="application/json,.json,text/csv,.csv"
            onChange={handleImport}
            style={{ display: 'none' }}
            disabled={isImporting}
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState />;
  }

  const {
    totalLinksProcessed,
    validStoresFound,
    storesWithFreeProducts,
    results
  } = batchData;

  if (results.length === 0) {
    return (
      <div className="batch-results">
        <BatchSummary
          totalLinksProcessed={totalLinksProcessed}
          validStoresFound={validStoresFound}
          storesWithFreeProducts={storesWithFreeProducts}
          onExport={handleExport}
          isExporting={isExporting}
          exportProgress={exportProgress}
        />
        <div className="no-results-message">
          No stores with free products were found.
        </div>
      </div>
    );
  }

  return (
    <div className="batch-results">
      <BatchSummary
        totalLinksProcessed={totalLinksProcessed}
        validStoresFound={validStoresFound}
        storesWithFreeProducts={storesWithFreeProducts}
        onExport={handleExport}
        isExporting={isExporting}
        exportProgress={exportProgress}
      />

      <div className="memory-controls">
        <button 
          className="memory-clear-button"
          onClick={clearMemory}
          disabled={expandedStores.size === 0}
        >
          <i className="fas fa-broom"></i> Clear Memory
        </button>
        {memoryWarning && (
          <div className="memory-warning">
            <i className="fas fa-exclamation-triangle"></i> {memoryWarning}
          </div>
        )}
      </div>

      <Pagination />

      <div className="search-container">
        <div className="search-input-wrapper">
          <i className="fas fa-search search-icon"></i>
          <input
            type="text"
            className="search-input"
            placeholder="Search stores and products..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {searchQuery && (
            <button
              className="clear-search"
              onClick={handleClearSearch}
              title="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        <div className="results-count">
          Showing {paginatedResults.length} of {results.length} stores
          {filteredResults.length !== results.length && 
            ` (${filteredResults.length} matches)`}
        </div>
      </div>

      {paginatedResults.map((store) => {
        // Use cached store data if available, otherwise use minimal data
        const fullStoreData = storeDetailsCache.get(store.storeUrl) || store;

        return (
          <StoreSection
            key={store.storeUrl}
            store={fullStoreData}
            isExpanded={expandedStores.has(store.storeUrl)}
            onToggle={toggleStore}
            searchQuery={debouncedSearchQuery}
            productCount={store.productCount}
          />
        );
      })}

      <Pagination />
    </div>
  );
};

export default React.memo(BatchResults); 